import { VisitSummaryData } from '../summary/VisitSummaryCard';

/**
 * Generates a visit summary from inspection data
 * @param inspectionData The raw inspection data
 * @returns A formatted VisitSummaryData object
 */
export const generateVisitSummary = (inspectionData: any): VisitSummaryData => {
  // Extract date and time
  const inspectionDate = new Date(inspectionData.inspection_date || new Date());
  const dateOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  };
  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  };

  const formattedDate = inspectionDate.toLocaleDateString('en-US', dateOptions);
  const formattedTime = inspectionDate.toLocaleTimeString('en-US', timeOptions);

  // Count question responses
  const questions = inspectionData.questions || [];
  const answers = inspectionData.answers || {};

  const totalQuestions = questions.length;
  const answeredQuestions = Object.keys(answers).length;

  let compliantAnswers = 0;
  let nonCompliantAnswers = 0;
  let naAnswers = 0;

  // Process questions and answers
  const processedQuestions = questions.map((question: any) => {
    const answer = answers[question.id] || 'Unanswered';
    let isCompliant = false;

    if (answer === 'Yes' || answer === 'Compliant') {
      compliantAnswers++;
      isCompliant = true;
    } else if (answer === 'No' || answer === 'Non-Compliant') {
      nonCompliantAnswers++;
      isCompliant = false;
    } else if (answer === 'N/A') {
      naAnswers++;
      isCompliant = true; // N/A is considered compliant
    }

    return {
      id: question.id,
      question: question.question_en || question.question,
      answer,
      isCompliant
    };
  });
  const cryptoRandomNumber = (max) => {
    const maxUint32 = 0xFFFFFFFF;
    const limit = maxUint32 - (maxUint32 % max);
    const randArray = new Uint32Array(1);
  
    while (true) {
      crypto.getRandomValues(randArray);
      if (randArray[0] < limit) {
        return randArray[0] % max;
      }
    }
  };

  // Create the summary data object
  const summaryData: VisitSummaryData = {
    date: formattedDate,
    time: formattedTime,

    taskId: inspectionData.task_id || 'T-' + cryptoRandomNumber(10000),
    taskType: inspectionData.inspection_type?.name_en || 'Regular Inspection',
    taskStatus: inspectionData.status || 'Completed',
    riskScore: inspectionData.risk_grade || 'C',

    establishment: {
      name: inspectionData.establishment?.name_en || 'Sample Establishment',
      id: inspectionData.establishment_id || 'EST-' + cryptoRandomNumber(10000),
      licenseNumber: inspectionData.establishment?.license_number || 'LIC-' + cryptoRandomNumber(10000),
      licenseExpiry: inspectionData.establishment?.license_expiry || '2025-12-31',
      type: inspectionData.establishment?.establishment_type || 'Commercial',
      nationality: inspectionData.establishment?.nationality || 'UAE'
    },

    location: {
      emirate: inspectionData.emirate?.name_en || 'Dubai',
      zone: inspectionData.zone || 'Business Bay',
      coordinates: {
        latitude: inspectionData.coordinates?.latitude || 25.2048,
        longitude: inspectionData.coordinates?.longitude || 55.2708
      }
    },

    inspectionSummary: {
      totalQuestions,
      answeredQuestions,
      compliantAnswers,
      nonCompliantAnswers,
      naAnswers
    },

    questions: processedQuestions
  };

  // Add incident information if available
  if (inspectionData.incident) {
    summaryData.incident = {
      subject: inspectionData.incident.subject || 'Workplace Safety Incident',
      date: new Date(inspectionData.incident.date || new Date()).toLocaleDateString('en-US', dateOptions),
      description: inspectionData.incident.description || 'No description provided',
      riskFactors: inspectionData.incident.risk_factors || ['Inadequate safety measures', 'Lack of training']
    };
  }

  return summaryData;
};

/**
 * Generates a sample visit summary for demonstration purposes
 * @returns A sample VisitSummaryData object
 */
export const generateSampleVisitSummary = (): VisitSummaryData => {
  const sampleData = {
    inspection_date: new Date(),
    task_id: 'T-5432',
    inspection_type: {
      name_en: 'Workplace Safety Inspection'
    },
    status: 'Completed',
    risk_grade: 'B',

    establishment: {
      name_en: 'Al Falah Construction Company',
      license_number: 'LIC-78945',
      license_expiry: '2025-06-30',
      establishment_type: 'Construction',
      nationality: 'UAE'
    },

    establishment_id: 'EST-12345',

    emirate: {
      name_en: 'Abu Dhabi'
    },
    zone: 'Industrial Area',
    coordinates: {
      latitude: 24.4539,
      longitude: 54.3773
    },

    incident: {
      subject: 'Worker Safety Violation',
      date: new Date(new Date().setDate(new Date().getDate() - 5)),
      description: 'Multiple workers observed without proper safety equipment on the construction site. Site manager was notified and immediate corrective action was taken.',
      risk_factors: [
        'Inadequate safety equipment',
        'Lack of safety training',
        'Non-compliance with safety regulations',
        'High-risk construction activities'
      ]
    },

    questions: [
      {
        id: 1,
        question_en: 'Are all workers wearing proper safety equipment?',
        is_mandatory: true
      },
      {
        id: 2,
        question_en: 'Is the construction site properly secured?',
        is_mandatory: true
      },
      {
        id: 3,
        question_en: 'Are safety signs clearly visible and in good condition?',
        is_mandatory: true
      },
      {
        id: 4,
        question_en: 'Is there a first aid kit available on site?',
        is_mandatory: true
      },
      {
        id: 5,
        question_en: 'Are emergency procedures posted and accessible to all workers?',
        is_mandatory: false
      },
      {
        id: 6,
        question_en: 'Is there adequate ventilation in enclosed work areas?',
        is_mandatory: false
      },
      {
        id: 7,
        question_en: 'Are electrical systems properly installed and maintained?',
        is_mandatory: true
      },
      {
        id: 8,
        question_en: 'Is there a designated safety officer on site?',
        is_mandatory: true
      }
    ],

    answers: {
      1: 'No',
      2: 'Yes',
      3: 'Yes',
      4: 'Yes',
      5: 'No',
      6: 'N/A',
      7: 'Yes',
      8: 'Yes'
    }
  };

  return generateVisitSummary(sampleData);
};

export default generateVisitSummary;
